<template>
  <div class="qr-code-generator">
    <div class="qr-form-container">
      <v-text-field
        v-model="text"
        :label="label"
        variant="outlined"
        color="success"
        bg-color="rgba(30, 30, 40, 0.5)"
        :type="inputType"
        :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append-inner="showPassword = !showPassword"
        class="qr-input"
      ></v-text-field>

      <v-btn
        color="success"
        variant="elevated"
        block
        @click="generateQRCode"
        class="generate-btn"
        :disabled="!text"
      >
        <v-icon start>mdi-qrcode</v-icon>
        QR-Code generieren
      </v-btn>
    </div>

    <v-slide-y-transition>
      <div v-if="showQR" class="qr-result">
        <div class="qr-container">
          <!-- Canvas wurde durch Image ersetzt -->
          <img v-if="qrDataUrl" :src="qrDataUrl" class="qr-image" alt="QR Code" />
          <div v-else class="qr-placeholder">QR-Code wird generiert...</div>
        </div>

        <div class="qr-actions mt-4">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-download"
                v-bind="props"
                class="action-btn"
                :disabled="!qrDataUrl"
              >
                Herunterladen
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(size, index) in printSizes"
                :key="index"
                @click="downloadQRCode(size.value)"
              >
                <v-list-item-title>{{ size.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn
                color="info"
                variant="tonal"
                prepend-icon="mdi-printer"
                v-bind="props"
                class="action-btn"
                :disabled="!qrDataUrl"
              >
                Drucken
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(size, index) in printSizes"
                :key="index"
                @click="printQRCode(size.value)"
              >
                <v-list-item-title>{{ size.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn
            color="error"
            variant="tonal"
            prepend-icon="mdi-refresh"
            @click="resetForm"
            class="action-btn"
          >
            Zurücksetzen
          </v-btn>
        </div>
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import QRCode from 'qrcode';
import { useToast } from 'vue-toastification';

const toast = useToast();

// Props mit Standardwerten
const props = defineProps({
  label: {
    type: String,
    default: 'Text für QR-Code'
  },
  secure: {
    type: Boolean,
    default: false
  }
});

// Reaktive Variablen
const text = ref('');
const showPassword = ref(false);
const showQR = ref(false);
const qrDataUrl = ref(''); // Speichert die Data URL des QR-Codes

// Druckgrößen
const printSizes = [
  { label: 'Micro (0.5x0.5 cm)', value: 'micro' },
  { label: 'Mini (1x1 cm)', value: 'mini' },
  { label: 'Klein (1.5x1.5 cm)', value: 'small' },
  { label: 'Mittel (2x2 cm)', value: 'medium' },
  { label: 'Groß (4x4 cm)', value: 'large' },
  { label: 'Maximal (6x6 cm)', value: 'full' }
];

// Berechnete Eigenschaft für den Input-Typ
const inputType = computed(() => {
  if (!props.secure) return 'text';
  return showPassword.value ? 'text' : 'password';
});

// QR-Code generieren
const generateQRCode = async () => {
  if (!text.value) {
    toast.warning('Bitte geben Sie einen Text ein.');
    return;
  }

  showQR.value = true;

  try {
    const options = {
      width: 300,
      margin: 1,
      color: {
        dark: '#4CAF50',  // Grün passend zum ARCM-Design
        light: '#ffffff'
      },
      errorCorrectionLevel: 'H' // Höchste Fehlerkorrektur für bessere Scanbarkeit
    };

    // QR-Code direkt als DataURL generieren
    qrDataUrl.value = await QRCode.toDataURL(text.value, options);
    toast.success('QR-Code erfolgreich generiert!');
  } catch (error) {
    console.error('Fehler bei der QR-Code-Generierung:', error);
    toast.error('Fehler bei der QR-Code-Generierung.');
    qrDataUrl.value = '';
  }
};

// QR-Code herunterladen
const downloadQRCode = async (size = 'medium') => {
  if (!qrDataUrl.value) {
    toast.warning('Kein QR-Code zum Herunterladen vorhanden.');
    return;
  }

  try {
    // Größen in Pixeln (bei 300 DPI)
    const sizeMap = {
      micro: { width: 59, height: 59 }, // 0.5x0.5 cm
      mini: { width: 118, height: 118 }, // 1x1 cm
      small: { width: 177, height: 177 }, // 1.5x1.5 cm
      medium: { width: 236, height: 236 }, // 2x2 cm
      large: { width: 472, height: 472 }, // 4x4 cm
      full: { width: 709, height: 709 } // 6x6 cm
    };

    const selectedSize = sizeMap[size] || sizeMap.medium;

    // Erstelle ein temporäres Canvas-Element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = selectedSize.width;
    canvas.height = selectedSize.height;

    // Lade das QR-Code-Bild
    const img = new window.Image();
    img.src = qrDataUrl.value;

    // Warte bis das Bild geladen ist
    await new Promise((resolve) => {
      img.onload = resolve;
    });

    // Zeichne das Bild auf das Canvas mit der gewünschten Größe
    ctx.drawImage(img, 0, 0, selectedSize.width, selectedSize.height);

    // Konvertiere das Canvas zu einer Data URL
    const resizedDataUrl = canvas.toDataURL('image/png');

    // Erstelle den Download-Link
    const link = document.createElement('a');
    link.download = `qrcode_${size}.png`;
    link.href = resizedDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`QR-Code in ${size} wird heruntergeladen!`);
  } catch (error) {
    console.error('Fehler beim Herunterladen des QR-Codes:', error);
    toast.error('Fehler beim Herunterladen des QR-Codes.');
  }
};

// QR-Code drucken
const printQRCode = (size = 'medium') => {
  if (!qrDataUrl.value) {
    toast.warning('Kein QR-Code zum Drucken vorhanden.');
    return;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Popup-Blocker verhindert das Öffnen des Druckfensters');
    return;
  }

  // Größen in Pixeln (bei 300 DPI)
  const sizeMap = {
    micro: { width: 59, height: 59 }, // 0.5x0.5 cm
    mini: { width: 118, height: 118 }, // 1x1 cm
    small: { width: 177, height: 177 }, // 1.5x1.5 cm
    medium: { width: 236, height: 236 }, // 2x2 cm
    large: { width: 472, height: 472 }, // 4x4 cm
    full: { width: 709, height: 709 } // 6x6 cm
  };

  const selectedSize = sizeMap[size] || sizeMap.medium;

  printWindow.document.write(`<!DOCTYPE html>
  <html>
    <head>
      <title>QR-Code Drucken</title>
      <style>
        body {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          margin: 0;
          background: white;
        }
        .qr-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        img {
          width: ${selectedSize.width}px;
          height: ${selectedSize.height}px;
          object-fit: contain;
        }
        .qr-text {
          font-family: Arial, sans-serif;
          font-size: 12px;
          color: #333;
          text-align: center;
          max-width: ${selectedSize.width}px;
          word-wrap: break-word;
        }
        @media print {
          body {
            height: auto;
            padding: 20px;
          }
          .qr-container {
            page-break-after: always;
          }
        }
      </style>
    </head>
    <body>
      <div class="qr-container">
        <img src="${qrDataUrl.value}" alt="QR Code">
        ${!props.secure ? `<div class="qr-text">${text.value}</div>` : ''}
      </div>
    </body>
  </html>`);

  printWindow.document.close();

  // Warten bis das Bild geladen ist, dann drucken
  printWindow.onload = function() {
    printWindow.focus();
    printWindow.print();
    setTimeout(() => {
      printWindow.close();
    }, 500);
  };

  toast.success(`QR-Code wird in ${size} gedruckt!`);
};

// Formular zurücksetzen
const resetForm = () => {
  text.value = '';
  showQR.value = false;
  qrDataUrl.value = '';
  toast.info('Formular wurde zurückgesetzt.');
};

// Wenn Text sich ändert und bereits ein QR-Code angezeigt wird, aktualisiere ihn
watch(text, () => {
  if (showQR.value) {
    generateQRCode();
  }
});
</script>

<style scoped>
.qr-code-generator {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.qr-form-container {
  background: rgba(25, 25, 30, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.qr-input {
  margin-bottom: 16px;
}

.generate-btn {
  font-weight: 500;
  height: 48px;
  transition: all 0.3s ease;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2) !important;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.3) !important;
}

.qr-result {
  background: rgba(25, 25, 30, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.qr-container {
  display: flex;
  justify-content: center;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  min-height: 300px;
  align-items: center;
}

.qr-image {
  width: 100%;
  max-width: 300px;
  height: auto;
}

.qr-placeholder {
  color: #4CAF50;
  font-weight: bold;
}

.qr-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.action-btn {
  flex: 1;
  min-width: 120px;
  transition: all 0.2s ease;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

@media (max-width: 600px) {
  .qr-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }
}
</style>
